import { BaseFeature } from '../../core/base-feature.js';

/**
 * DungeonCrawlerFeature (frontend)
 *
 * The commander side. Streams real-time paint/spawn/effect actions to the
 * desktop and receives 20Hz state updates back.
 */
export class DungeonCrawlerFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.currentState = null;
        this.stateCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'dungeon-crawler',
            name: 'Dungeon Crawler',
            version: 'v2.0',
            tier: 'external',
            capabilities: ['start', 'end', 'get_state', 'commander_paint', 'commander_spawn', 'commander_effect',
                'commander_expand_map', 'commander_teleport_player', 'commander_remove_monsters',
                'commander_place_pickup', 'commander_set_loot', 'commander_reroll_loot',
                'commander_place_chest', 'commander_reroll_chest', 'commander_heart_attack',
                'commander_spawn_chorus', 'commander_set_monster_respawn',
                'commander_set_floor_tilt', 'commander_camera_slip', 'commander_paint_fog',
                'commander_spawn_bloodline', 'commander_place_npc',
                'commander_possess_monster', 'commander_possess_input', 'commander_possess_release',
                'commander_gaze_edge_spawn'],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[DungeonCrawler] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this._notify(payload);
                break;
            case 'start_response':
            case 'end_response':
            case 'get_state_response':
            case 'commander_paint_response':
            case 'commander_spawn_response':
            case 'commander_effect_response':
            case 'commander_expand_map_response':
            case 'commander_teleport_player_response':
            case 'commander_remove_monsters_response':
            case 'commander_place_pickup_response':
            case 'commander_set_loot_response':
            case 'commander_reroll_loot_response':
            case 'commander_place_chest_response':
            case 'commander_reroll_chest_response':
            case 'commander_heart_attack_response':
            case 'commander_spawn_chorus_response':
            case 'commander_set_monster_respawn_response':
            case 'commander_set_floor_tilt_response':
            case 'commander_camera_slip_response':
            case 'commander_paint_fog_response':
            case 'commander_spawn_bloodline_response':
            case 'commander_place_npc_response':
            case 'commander_possess_monster_response':
            case 'commander_possess_release_response':
            case 'commander_gaze_edge_spawn_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this._pendingRequests.has(originalId)) {
                    const { resolve } = this._pendingRequests.get(originalId);
                    this._pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }
            default:
                console.warn(`[DungeonCrawler] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this._pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);
            this._pendingRequests.set(messageId, {
                resolve: (result) => { clearTimeout(timeout); resolve(result); },
                reject
            });
        });
    }

    // ─── Lifecycle ───
    startGame(config = {}) { return this._sendRequest('start', config); }
    endGame()              { return this._sendRequest('end'); }
    getState()             { return this._sendRequest('get_state'); }

    // ─── Commander actions ───
    // Fire-and-forget so brush strokes don't await round-trips
    paint(brush, x, y) {
        this.sendMessage('commander_paint', { brush, x, y });
    }
    spawn(type, x, y) {
        this.sendMessage('commander_spawn', { type, x, y });
    }
    effect(kind, opts = {}) {
        this.sendMessage('commander_effect', { kind, ...opts });
    }
    expandMap(direction, size = 28) {
        return this._sendRequest('commander_expand_map', { direction, size });
    }
    teleportPlayer(x, y) {
        this.sendMessage('commander_teleport_player', { x, y });
    }
    removeMonsters(ids) {
        this.sendMessage('commander_remove_monsters', { ids });
    }
    placePickup(type, x, y, opts = {}) {
        this.sendMessage('commander_place_pickup', { type, x, y, mimic: !!opts.mimic, mimicType: opts.mimicType || null });
    }
    setLoot(monsterId, loot) {
        return this._sendRequest('commander_set_loot', { monsterId, loot });
    }
    rerollLoot(monsterId) {
        return this._sendRequest('commander_reroll_loot', { monsterId });
    }
    placeChest(x, y, opts = {}) {
        return this._sendRequest('commander_place_chest', { x, y, requiresEyeLock: !!opts.requiresEyeLock });
    }
    gazeEdgeSpawn(type = 'chaser', edgeRadius = 4.0) {
        return this._sendRequest('commander_gaze_edge_spawn', { type, edgeRadius });
    }
    rerollChest(chestId) {
        return this._sendRequest('commander_reroll_chest', { chestId });
    }
    heartAttack(type = 'chaser') {
        return this._sendRequest('commander_heart_attack', { type });
    }
    spawnChorus(x, y, type = 'chaser') {
        return this._sendRequest('commander_spawn_chorus', { x, y, type });
    }
    setMonsterRespawn(monsterId, delaySec, count) {
        return this._sendRequest('commander_set_monster_respawn', { monsterId, delaySec, count });
    }
    setFloorTilt(intensity, angle) {
        this.sendMessage('commander_set_floor_tilt', { intensity, angle });
    }
    cameraSlip(opts = {}) {
        return this._sendRequest('commander_camera_slip', opts);
    }
    paintFog(x, y, value, radius = 1) {
        this.sendMessage('commander_paint_fog', { x, y, value, radius });
    }
    spawnBloodline(bloodlineIndex, x, y) {
        return this._sendRequest('commander_spawn_bloodline', { bloodlineIndex, x, y });
    }
    placeNpc(type, x, y) {
        return this._sendRequest('commander_place_npc', { type, x, y });
    }
    possessMonster(monsterId, durationSec) {
        return this._sendRequest('commander_possess_monster', { monsterId, durationSec });
    }
    possessInput(keys) {
        // Fire-and-forget — high-frequency input shouldn't await round-trips
        this.sendMessage('commander_possess_input', { keys });
    }
    possessRelease() {
        return this._sendRequest('commander_possess_release', {});
    }

    // ─── Subscriptions ───
    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    _notify(data) {
        for (const cb of this.stateCallbacks) {
            try { cb(data); } catch (err) { console.error('[DungeonCrawler] state callback:', err); }
        }
    }

    getCurrentState() { return this.currentState; }

    async cleanup() {
        this.currentState = null;
        this.stateCallbacks.clear();
        for (const [, { reject }] of this._pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this._pendingRequests.clear();
    }
}
